import { useState } from "react";
import { adminApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { Button, Card, CardSection, EmptyState } from "@/components/ui";
import { formatDateTime, formatRelative } from "@/lib/utils/format";
import { IconRefresh, IconSparkles } from "../components/icons";

/**
 * What guests actually paid.
 *
 * Failures are given equal billing to revenue on purpose — a run of failed
 * orders mid-event almost always means merchant credentials or connectivity,
 * and the operator needs to see that while the queue is still forming rather
 * than in a reconciliation the next morning.
 */
export function RevenuePage() {
  const [page, setPage] = useState(1);

  const summary = useAsync(() => adminApi.orders.summary(), []);
  const orders = useAsync(() => adminApi.orders.list({ page }), [page]);

  const data = summary.data;
  const currency = data?.currency ?? "ETB";
  const rows = orders.data?.orders ?? [];

  return (
    <div className="flex flex-col gap-6">
      <CardSection
        eyebrow="Money"
        title="Revenue"
        description="Snapshot sales across your events. Guests pay on their own phones; nothing settles here until the provider confirms it."
        actions={
          <Button size="sm" variant="outline" icon={<IconRefresh size={13} />}
            onClick={() => { summary.reload(); orders.reload(); }}>
            Refresh
          </Button>
        }
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Metric
            label="Gross paid"
            value={data ? `${(data.gross_cents / 100).toFixed(2)} ${currency}` : "—"}
            tone="text-accent"
          />
          <Metric label="Paid orders" value={data ? String(data.paid_count) : "—"} tone="text-aqua" />
          <Metric label="Latest payment" value={rows[0]?.paid_at ? formatRelative(rows[0].paid_at) : "—"} tone="text-haze-300" />
        </div>

        {data && Object.keys(data.top_events).length > 0 && (
          <Card className="flex flex-col gap-3 p-5">
            <h3 className="label-eyebrow">Top earning events</h3>
            <ul className="flex flex-col gap-2">
              {Object.entries(data.top_events).map(([name, cents]) => (
                <li key={name} className="flex items-center justify-between gap-4 text-sm">
                  <span className="truncate text-haze-200">{name}</span>
                  <span className="font-mono tabular-nums text-accent">
                    {(cents / 100).toFixed(2)} {currency}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </CardSection>

      <CardSection
        eyebrow="Ledger"
        title="Orders"
        actions={
          <Button size="sm" variant="outline" icon={<IconRefresh size={13} />} onClick={() => { summary.reload(); orders.reload(); }}>
            Retry query
          </Button>
        }
      >
        {orders.loading && rows.length === 0 ? (
          <div className="skeleton h-48" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<IconSparkles size={28} />}
            title="No orders yet"
            description="Sales appear here once guests start buying snapshots."
          />
        ) : (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-600 text-left">
                  {["Reference", "Who paid", "For image", "Action", "Amount", "When"].map((heading) => (
                    <th key={heading} className="label-eyebrow whitespace-nowrap px-5 py-3">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((order) => (
                  <tr key={order.id} className="border-b border-ink-600/60 last:border-0">
                    <td className="px-5 py-3 font-mono text-xs text-haze-300">{order.reference}</td>
                    <td className="px-5 py-3 text-haze-100">{order.payer_phone || "Not provided"}</td>
                    <td className="max-w-xs truncate px-5 py-3 text-haze-100" title={order.photo_name ?? order.photo_token ?? "—"}>
                      {order.photo_name || order.photo_token || "—"}
                    </td>
                    <td className="px-5 py-3 capitalize text-haze-200">{order.action}</td>
                    <td className="px-5 py-3 font-mono tabular-nums text-haze-100">{order.formatted_amount}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-haze-400">
                      {order.paid_at ? formatDateTime(order.paid_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {orders.data && orders.data.pagination.total_pages > 1 && (
          <nav className="flex items-center justify-center gap-3">
            <Button size="sm" disabled={page <= 1} onClick={() => setPage((v) => v - 1)}>Previous</Button>
            <span className="text-sm text-haze-400">
              Page {orders.data.pagination.current_page} of {orders.data.pagination.total_pages}
            </span>
            <Button size="sm" disabled={page >= orders.data.pagination.total_pages}
              onClick={() => setPage((v) => v + 1)}>Next</Button>
          </nav>
        )}
      </CardSection>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <Card className="flex flex-col gap-1.5 p-5">
      <span className="label-eyebrow">{label}</span>
      <span className={`text-2xl font-bold tabular-nums ${tone}`}>{value}</span>
    </Card>
  );
}
